import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { MicrophoneStage } from "@phosphor-icons/react";
import client from "@/lib/api";

// Legal & Trust Readiness (Phase 1) — a distinct, one-time voice consent,
// separate from the general "Use AI features" switch, since this one is
// specifically about sending a recording of your actual voice (and
// receiving synthesized speech back) to our voice provider (OpenAI). Shown
// once, before the FIRST mic tap in a session; the backend records
// acceptance at GET /api/legal/ai-consent-info's "needs_voice_consent" /
// POST /api/legal/accept {docs:["voice_consent"]} (legal_consent.py).
let _cachedNeedsConsent = null; // per-tab memo so we don't re-fetch on every tap

// Legal & Trust Readiness (Phase 1): a 2nd account signing in on the same
// tab must never inherit the 1st account's "already consented" memo.
export function resetVoiceConsentCache() {
  _cachedNeedsConsent = null;
}

async function needsVoiceConsent() {
  if (_cachedNeedsConsent === false) return false;
  try {
    const { data } = await client.get("/legal/ai-consent-info");
    _cachedNeedsConsent = !!data.needs_voice_consent;
  } catch {
    _cachedNeedsConsent = false; // fail open on a transient network error
  }
  return _cachedNeedsConsent;
}

async function acceptVoiceConsent() {
  _cachedNeedsConsent = false;
  try { await client.post("/legal/accept", { docs: ["voice_consent"] }); } catch {}
}

function VoiceConsentModal({ onAccept, onDecline }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/70 p-4" data-testid="voice-consent-modal">
      <div className="w-full max-w-sm rounded-[18px] border border-white/10 bg-obsidian p-6 shadow-2xl">
        <div className="mb-3 flex items-center gap-2.5">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-gold/[0.14]"><MicrophoneStage size={18} className="text-gold" /></span>
          <h3 className="font-display text-lg font-semibold text-[#F6F5F1]">Enable BARI's voice?</h3>
        </div>
        <p className="text-[13.5px] leading-relaxed text-dim">
          Your spoken words are sent to our voice provider (OpenAI) to transcribe into text, and BARI's
          replies are sent there to generate spoken audio. This is separate from the "Use AI features"
          switch in Settings — you only need to confirm this once.
        </p>
        <div className="mt-5 flex gap-2.5">
          <button onClick={onDecline} data-testid="voice-consent-decline"
            className="flex-1 rounded-full border border-white/15 py-2.5 text-[13.5px] font-bold text-dim hover:border-white/30">
            Not now
          </button>
          <button onClick={onAccept} data-testid="voice-consent-accept"
            className="flex-1 rounded-full bg-gold py-2.5 text-[13.5px] font-bold text-gold-ink hover:opacity-90">
            Allow voice
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// Wraps any async action that's about to use voice (mic recording OR
// spoken-reply playback) with the one-time consent check above. Returns a
// function with the SAME signature that only proceeds once consent exists.
export function useVoiceConsentGate() {
  const [pending, setPending] = useState(null); // {resolve} while the modal is open
  const resolverRef = useRef(null);

  const gate = () =>
    new Promise((resolve) => {
      needsVoiceConsent().then((needs) => {
        if (!needs) return resolve(true);
        resolverRef.current = resolve;
        setPending(true);
      });
    });

  const modal = pending ? (
    <VoiceConsentModal
      onAccept={async () => { await acceptVoiceConsent(); setPending(null); resolverRef.current?.(true); }}
      onDecline={() => { setPending(null); resolverRef.current?.(false); }}
    />
  ) : null;

  return [gate, modal];
}
